import { looksLikeTechnicalAdminError } from './adminApiErrorPolicy';

type AxiosLikeError = {
  code?: string;
  message?: string;
  response?: { status?: number; statusText?: string; data?: unknown };
};

/** Browser axios often surfaces connection failures as ERR_NETWORK / "Network Error" (no response). */
export function isAxiosNetworkUnreachableError(err: unknown): boolean {
  if (err == null || typeof err !== 'object') return false;
  const ax = err as AxiosLikeError;
  if (ax.response) return false;
  const msg = typeof ax.message === 'string' ? ax.message : '';
  const code = typeof ax.code === 'string' ? ax.code : '';
  return (
    code === 'ECONNREFUSED' ||
    code === 'ECONNRESET' ||
    code === 'ERR_NETWORK' ||
    code === 'ERR_CONNECTION_REFUSED' ||
    msg.includes('ECONNREFUSED') ||
    msg.includes('ECONNRESET') ||
    msg.includes('ERR_CONNECTION_REFUSED') ||
    /^network error$/i.test(msg.trim())
  );
}

/** Best-effort user string from orchestrator-style JSON bodies. */
export function messageFromAxiosErrorPayload(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.success === false && d.error && typeof d.error === 'object') {
    const msg = (d.error as Record<string, unknown>).message;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
  }
  if (typeof d.message === 'string' && d.message.trim()) return d.message.trim();
  return null;
}

const OPERATOR_TECHNICAL_FALLBACK =
  'Something went wrong talking to the orchestrator. Please try again or check service logs.';

/** `Application not found: <uuid>` and `Application <uuid> not found` (orchestrator NOT_FOUND). */
const APPLICATION_MISSING_MESSAGE =
  /application(?:\s+not\s+found(?::\s*[0-9a-f-]{36})?|\s+[0-9a-f-]{36}\s+not\s+found)/i;

export const APPLICATION_NOT_FOUND_ADMIN_TITLE = 'Application not found';

export const APPLICATION_NOT_FOUND_ADMIN_BODY =
  'The requested application could not be found. Please verify the application ID or return to the Applications list and try again.';

function orchestratorErrorMessageFromPayload(data: unknown): string | null {
  const parsed = messageFromAxiosErrorPayload(data);
  if (parsed) return parsed;
  if (!data || typeof data !== 'object') return null;
  const o = data as Record<string, unknown>;
  if (typeof o.error === 'string' && o.error.trim()) return o.error.trim();
  if (typeof o.message === 'string' && o.message.trim()) return o.message.trim();
  return null;
}

/** True when orchestrator reports the application row does not exist. */
export function isApplicationNotFoundAxiosError(err: unknown): boolean {
  const ax = err as AxiosLikeError;
  const status = ax.response?.status;
  if (status !== 404 && status !== 400) return false;
  const payloadMsg = orchestratorErrorMessageFromPayload(ax.response?.data);
  const msg = payloadMsg ?? (typeof ax.message === 'string' ? ax.message : '');
  if (!msg.trim()) return status === 404;
  if (APPLICATION_MISSING_MESSAGE.test(msg)) return true;
  return status === 404 && /application not found/i.test(msg);
}

function scrubOperatorFacingMessage(message: string): string {
  if (APPLICATION_MISSING_MESSAGE.test(message) || /^application not found\.?$/i.test(message.trim())) {
    return APPLICATION_NOT_FOUND_ADMIN_TITLE;
  }
  if (looksLikeTechnicalAdminError(message)) {
    return OPERATOR_TECHNICAL_FALLBACK;
  }
  return message;
}

/** True when the failed request targeted admin-api (not orchestrator). */
export function isAdminConsoleAxiosError(err: unknown): boolean {
  const ax = err as { config?: { baseURL?: string; url?: string } };
  const base = typeof ax.config?.baseURL === 'string' ? ax.config.baseURL : '';
  const url = typeof ax.config?.url === 'string' ? ax.config.url : '';
  return (
    /\/admin\/api\b/.test(base) ||
    /:4000\b/.test(base) ||
    url.includes('/admin/api') ||
    url.includes('/auth/provision-operator')
  );
}

/** MFA finish / provision failures may come from orch or admin-api. */
export function messageFromMfaCompletionError(err: unknown): string {
  if (isAdminConsoleAxiosError(err)) {
    return messageFromAdminConsoleAxiosError(err);
  }
  return messageFromOrchestratorAxiosError(err);
}

/** Human-readable message for orchestrator axios failures (session login, doctrine, …). */
export function messageFromOrchestratorAxiosError(err: unknown): string {
  const ax = err as AxiosLikeError;
  const msg = typeof ax.message === 'string' ? ax.message : '';
  if (isAxiosNetworkUnreachableError(err)) {
    return 'Cannot reach the orchestrator (connection refused). Start it on :3000 or check VITE_ORCHESTRATOR_PROXY_TARGET / VITE_ORCHESTRATOR_API_BASE in apps/admin/.env.';
  }
  if (ax.code === 'ECONNREFUSED' || msg.includes('ECONNREFUSED')) {
    return 'Cannot reach the orchestrator (connection refused). Start it on :3000 or check VITE_ORCHESTRATOR_PROXY_TARGET / VITE_ORCHESTRATOR_API_BASE in apps/admin/.env.';
  }
  const status = ax.response?.status;
  const statusText = ax.response?.statusText ?? '';
  if (status === 500 && /internal server error/i.test(statusText)) {
    return 'Cannot reach the orchestrator (dev proxy error). Start the orchestrator on port 3000 and confirm VITE_ORCHESTRATOR_PROXY_TARGET in apps/admin/.env.';
  }
  if (ax.code === 'ECONNRESET' || msg.includes('ECONNRESET')) {
    return 'The orchestrator closed the connection. Check the orchestrator terminal for errors.';
  }
  const data = ax.response?.data;
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (typeof o.error === 'string' && o.error.trim()) {
      return scrubOperatorFacingMessage(o.error.trim());
    }
    if (typeof o.message === 'string' && o.message.trim()) {
      return scrubOperatorFacingMessage(o.message.trim());
    }
  }
  if (typeof status === 'number') {
    const base = userMessageForAdminHttp(status, data);
    if (status >= 500 && statusText) {
      return scrubOperatorFacingMessage(`${base} (${statusText})`);
    }
    return scrubOperatorFacingMessage(base);
  }
  return scrubOperatorFacingMessage(msg.trim() || 'Request failed.');
}

/** Human-readable message for admin-api / admin-console axios failures (login, vendors, …). */
export function messageFromAdminConsoleAxiosError(err: unknown): string {
  const ax = err as AxiosLikeError;
  const msg = typeof ax.message === 'string' ? ax.message : '';
  if (isAxiosNetworkUnreachableError(err)) {
    return 'Cannot reach the admin API (connection refused). Start admin-api (default http://localhost:4000/admin/api) or set VITE_ADMIN_API_BASE in apps/admin/.env.';
  }
  if (ax.code === 'ECONNREFUSED' || msg.includes('ECONNREFUSED')) {
    return 'Cannot reach the admin API (connection refused). Start admin-api (default http://localhost:4000/admin/api) or set VITE_ADMIN_API_BASE in apps/admin/.env.';
  }
  if (ax.code === 'ECONNRESET' || msg.includes('ECONNRESET')) {
    return 'The admin API closed the connection (often a server crash). Check the admin-api terminal for errors.';
  }
  const status = ax.response?.status;
  const data = ax.response?.data;
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (typeof o.error === 'string' && o.error.trim()) return o.error.trim();
    if (typeof o.message === 'string' && o.message.trim()) return o.message.trim();
  }
  if (typeof status === 'number') {
    const st = ax.response?.statusText;
    if (status === 404) {
      return 'Admin API route not found. Set VITE_ADMIN_API_BASE to the mount root (e.g. http://localhost:4000/admin/api) and restart admin-api with ADMIN_API_MOUNT_PATH=/admin/api.';
    }
    const base = userMessageForAdminHttp(status, data);
    if (status >= 500 && st) return `${base} (${st})`;
    return base;
  }
  return msg.trim() || 'Request failed.';
}

/**
 * Reads throttle hints from orchestrator-style JSON bodies (and `Retry-After` header)
 * so OTP / reset flows can seed cooldown timers after HTTP 429.
 */
export function throttleHintsFromOrchestratorAxiosError(err: unknown): {
  retryAfterSeconds?: number;
  cooldownUntil?: string;
} {
  const ax = err as {
    response?: {
      status?: number;
      headers?: { get?: (key: string) => string | undefined } & Record<string, string>;
      data?: unknown;
    };
  };
  const data = ax.response?.data;
  let retryAfterSeconds: number | undefined;
  let cooldownUntil: string | undefined;

  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const details =
      o.details && typeof o.details === 'object' ? (o.details as Record<string, unknown>) : null;
    const ra =
      typeof o.retryAfterSeconds === 'number'
        ? o.retryAfterSeconds
        : typeof details?.retry_after_seconds === 'number'
          ? details.retry_after_seconds
          : undefined;
    const cu =
      typeof o.cooldownUntil === 'string'
        ? o.cooldownUntil
        : typeof details?.cooldown_until === 'string'
          ? details.cooldown_until
          : undefined;
    if (typeof ra === 'number' && Number.isFinite(ra)) retryAfterSeconds = ra;
    if (typeof cu === 'string') cooldownUntil = cu;
  }

  const headers = ax.response?.headers;
  if (retryAfterSeconds === undefined && headers) {
    const raw =
      typeof headers.get === 'function'
        ? headers.get('retry-after')
        : (headers as Record<string, string>)['retry-after'];
    if (raw) {
      const n = parseInt(String(raw), 10);
      if (Number.isFinite(n)) retryAfterSeconds = n;
    }
  }

  return { retryAfterSeconds, cooldownUntil };
}

export function userMessageForAdminHttp(status: number, data: unknown): string {
  const parsed = messageFromAxiosErrorPayload(data);
  if (parsed) {
    if (looksLikeTechnicalAdminError(parsed)) {
      return OPERATOR_TECHNICAL_FALLBACK;
    }
    return parsed.slice(0, 800);
  }
  if (status === 403) return 'You do not have permission for this action.';
  if (status === 404) return 'Resource not found.';
  if (status === 429) return 'Too many requests. Try again shortly.';
  if (status >= 500) return 'Server error. Please try again later.';
  return `Request failed (${status}).`;
}
